// src/pages/products/ManageProductAttributesPage.jsx
import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreVertical,
  AlertCircle,
  RefreshCw,
  Save,
  X,
  Hash
} from 'lucide-react';
import { 
  getProductAttributes, 
  createProductAttribute, 
  getProductAttribute, 
  updateProductAttribute, 
  deleteProductAttribute 
} from '../../services/api/attributes';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './ManageProductAttributesPage.module.css';

const ManageProductAttributesPage = () => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  // State management
  const [attributes, setAttributes] = useState([]);
  const [filteredAttributes, setFilteredAttributes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Modal/Form states
  const [showForm, setShowForm] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    values: [{ id: null, value: '' }] // Store objects with id and value
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load attributes on component mount
  useEffect(() => {
    fetchAttributes();
  }, []);

  // Filter attributes when search query or attributes change
  useEffect(() => {
    filterAttributes();
  }, [attributes, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAttributes = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      const response = await getProductAttributes();
      setAttributes(response.attributes || []);
    } catch (err) {
      console.error('Failed to fetch attributes:', err);
      setLoadingError(t('errors.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const filterAttributes = () => {
    if (!searchQuery.trim()) {
      setFilteredAttributes(attributes);
      return;
    }

    const filtered = attributes.filter(attribute =>
      attribute.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (attribute.values && attribute.values.some(value => 
        value.value.toLowerCase().includes(searchQuery.toLowerCase())
      ))
    );
    setFilteredAttributes(filtered);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('attributes.nameRequired');
    } else if (formData.name.trim().length < 2) {
      errors.name = t('attributes.nameMinLength');
    }

    // Check for non-empty values
    const validValues = formData.values.filter(valueObj => valueObj.value.trim());
    if (validValues.length === 0) {
      errors.values = t('attributes.valuesRequired');
    }

    // Check for duplicate values
    const uniqueValues = new Set(validValues.map(v => v.value.toLowerCase()));
    if (uniqueValues.size !== validValues.length) {
      errors.values = t('attributes.duplicateValues');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', values: [{ id: null, value: '' }] });
    setFormErrors({});
    setEditingAttribute(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = async (attributeId) => {
    try {
      setIsSubmitting(true);
      const response = await getProductAttribute(attributeId);
      const attribute = response;
      
      setFormData({
        name: attribute.name || '',
        values: attribute.values && attribute.values.length > 0 
          ? attribute.values.map(v => ({ id: v.id, value: v.value }))
          : [{ id: null, value: '' }]
      });
      setEditingAttribute(attribute);
      setShowForm(true);
      setActiveDropdown(null);
    } catch (err) {
      console.error('Failed to fetch attribute details:', err);
      error(t('attributes.failedToLoadAttribute'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (attributeId, attributeName) => {
    const confirmed = await confirm(
      t('attributes.confirmDelete', { attributeName }),
      {
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await deleteProductAttribute(attributeId);
      setAttributes(prev => prev.filter(a => a.id !== attributeId));
      success(t('attributes.attributeDeletedSuccess', { attributeName }));
    } catch (err) {
      console.error('Failed to delete attribute:', err);
      error(t('attributes.failedToDelete'));
    }
    setActiveDropdown(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const submitData = {
        name: formData.name.trim(),
        values: formData.values
          .filter(valueObj => valueObj.value.trim()) // Filter out empty values
          .map(valueObj => {
            const cleanValue = { value: valueObj.value.trim() };
            // Include ID only if it exists (for existing values)
            if (valueObj.id) {
              cleanValue.id = valueObj.id;
            }
            return cleanValue;
          })
      };

      if (editingAttribute) {
        // Update existing attribute
        const response = await updateProductAttribute(editingAttribute.id, submitData);
        // Fetch fresh data to get updated values
        const updatedAttribute = await getProductAttribute(editingAttribute.id);
        setAttributes(prev => 
          prev.map(a => a.id === editingAttribute.id ? updatedAttribute : a)
        );
        success(t('attributes.attributeUpdatedSuccess', { attributeName: updatedAttribute.name }));
      } else {
        // Create new attribute - for new attributes, we don't need IDs
        const createData = {
          name: formData.name.trim(),
          values: formData.values
            .filter(valueObj => valueObj.value.trim())
            .map(valueObj => valueObj.value.trim())
        };
        const response = await createProductAttribute(createData);
        // Fetch fresh data to get the complete attribute with values
        const newAttribute = await getProductAttribute(response.attribute.id);
        setAttributes(prev => [...prev, newAttribute]);
        success(t('attributes.attributeCreatedSuccess', { attributeName: newAttribute.name }));
      }

      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save attribute:', err);
      if (err.response?.data?.message) {
        setFormErrors({ submit: err.response.data.message });
      } else {
        setFormErrors({ 
          submit: editingAttribute 
            ? t('attributes.failedToUpdate') 
            : t('attributes.failedToCreate') 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleValueChange = (index, value) => {
    const newValues = [...formData.values];
    newValues[index] = { ...newValues[index], value };
    setFormData(prev => ({ ...prev, values: newValues }));
    
    // Clear values error when user starts typing
    if (formErrors.values) {
      setFormErrors(prev => ({ ...prev, values: '' }));
    }
  };

  const addValue = () => {
    setFormData(prev => ({ 
      ...prev, 
      values: [...prev.values, { id: null, value: '' }] 
    }));
  };

  const removeValue = (index) => {
    if (formData.values.length > 1) {
      const newValues = formData.values.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, values: newValues }));
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    resetForm();
  };

  const toggleDropdown = (attributeId) => {
    setActiveDropdown(activeDropdown === attributeId ? null : attributeId);
  };

  if (isLoading) {
    return (
      <div className={styles.manageAttributesPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Tag className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('attributes.title')}</h1>
                <p>{t('attributes.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('attributes.loadingAttributes')}</h3>
          <p>{t('attributes.loadingAttributesDesc')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.manageAttributesPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Tag className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('attributes.title')}</h1>
                <p>{t('attributes.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('errors.somethingWentWrong')}</h3>
          <p>{loadingError}</p>
          <button onClick={fetchAttributes} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.manageAttributesPage}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Tag className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('attributes.title')}</h1>
              <p>{t('attributes.description')}</p>
            </div>
          </div>
          
          {hasPermission('create_product_attribute') && (
            <div className={styles.headerActions}>
              <button onClick={handleCreateNew} className={styles.primaryButton}>
                <Plus size={20} />
                {t('attributes.addAttribute')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className={styles.pageControls}>
        <div className={styles.searchSection}>
          <div className={styles.searchInputWrapper}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              placeholder={t('attributes.searchAttributes')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
        
        <div className={styles.controlsRight}>
          <div className={styles.resultsInfo}>
            {searchQuery ? (
              <span>{t('attributes.filteredResults', { 
                count: filteredAttributes.length,
                total: attributes.length 
              })}</span>
            ) : (
              <span>{t('attributes.totalAttributes', { count: attributes.length })}</span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.pageContent}>
        {filteredAttributes.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? (
              <>
                <Search className={styles.emptyIcon} size={48} />
                <h3>{t('attributes.noSearchResults')}</h3>
                <p>{t('attributes.noSearchResultsDesc')}</p>
                <button 
                  onClick={() => setSearchQuery('')} 
                  className={styles.secondaryButton}
                >
                  {t('common.clearSearch')}
                </button>
              </>
            ) : (
              <>
                <Tag className={styles.emptyIcon} size={48} />
                <h3>{t('attributes.noAttributes')}</h3>
                <p>{t('attributes.noAttributesDesc')}</p>
                {hasPermission('create_product_attribute') && (
                  <button onClick={handleCreateNew} className={styles.primaryButton}>
                    <Plus size={20} />
                    {t('attributes.createFirstAttribute')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.attributesTableContainer}>
            <table className={styles.attributesTable}>
              <thead>
                <tr>
                  <th className={styles.nameColumn}>{t('attributes.attributeName')}</th>
                  <th className={styles.valuesColumn}>{t('attributes.values')}</th>
                  <th className={styles.countColumn}>{t('attributes.valueCount')}</th>
                  <th className={styles.actionsColumn}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttributes.map((attribute) => (
                  <tr key={attribute.id} className={styles.attributeRow}>
                    <td className={styles.nameCell}>
                      <div className={styles.attributeInfo}>
                        <Tag className={styles.attributeIcon} size={16} />
                        <span className={styles.attributeName}>{attribute.name}</span>
                      </div>
                    </td>
                    
                    <td className={styles.valuesCell}>
                      <div className={styles.valuesList}>
                        {attribute.values && attribute.values.length > 0 ? (
                          attribute.values.slice(0, 3).map((value, index) => (
                            <span key={value.id || index} className={styles.valueTag}>
                              {value.value}
                            </span>
                          ))
                        ) : (
                          <span className={styles.noValues}>—</span>
                        )}
                        {attribute.values && attribute.values.length > 3 && (
                          <span className={styles.moreValues}>
                            +{attribute.values.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    
                    <td className={styles.countCell}>
                      <div className={styles.countInfo}>
                        <Hash size={12} />
                        <span>{attribute.values ? attribute.values.length : 0}</span>
                      </div>
                    </td>
                    
                    <td className={styles.actionsCell}>
                      <div className={styles.actionsWrapper}>
                        {hasPermission('edit_product_attribute') && (
                          <button 
                            onClick={() => handleEdit(attribute.id)}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            title={t('common.edit')}
                          >
                            <Edit size={14} />
                          </button>
                        )}
                        
                        {hasPermission('delete_product_attribute') && (
                          <div className={styles.dropdownWrapper}>
                            <button
                              className={`${styles.actionButton} ${styles.moreButton}`}
                              onClick={() => toggleDropdown(attribute.id)}
                              title={t('common.moreActions')}
                            >
                              <MoreVertical size={14} />
                            </button>
                            
                            {activeDropdown === attribute.id && (
                              <div className={styles.dropdownMenu}>
                                <button
                                  className={`${styles.dropdownItem} ${styles.deleteItem}`}
                                  onClick={() => handleDelete(attribute.id, attribute.name)}
                                >
                                  <Trash2 size={14} />
                                  {t('common.delete')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingAttribute ? t('attributes.editAttribute') : t('attributes.createNewAttribute')}
              </h2>
              <button onClick={handleCancelForm} className={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            {formErrors.submit && (
              <div className={styles.errorBanner}>
                <AlertCircle size={20} />
                <p>{formErrors.submit}</p>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label htmlFor="name">{t('attributes.attributeName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('attributes.attributeNamePlaceholder')}
                  className={formErrors.name ? 'error' : ''}
                  required
                  autoFocus
                />
                {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
              </div>

              <div className={styles.formGroup}>
                <label>{t('attributes.values')} *</label>
                <div className={styles.valuesContainer}>
                  {formData.values.map((valueObj, index) => (
                    <div key={index} className={styles.valueInputWrapper}>
                      <input
                        type="text"
                        value={valueObj.value}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        placeholder={t('attributes.valuePlaceholder', { index: index + 1 })}
                        className={formErrors.values ? 'error' : ''}
                      />
                      {formData.values.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeValue(index)}
                          className={styles.removeValueButton}
                          title={t('attributes.removeValue')}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addValue}
                    className={styles.addValueButton}
                  >
                    <Plus size={16} />
                    {t('attributes.addValue')}
                  </button>
                </div>
                {formErrors.values && <span className={styles.errorText}>{formErrors.values}</span>}
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={handleCancelForm}
                  className={styles.cancelButton}
                >
                  <X size={18} />
                  {t('common.cancel')}
                </button>
                <button 
                  type="submit" 
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className={styles.loadingSpinner}></div>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingAttribute ? t('attributes.updateAttribute') : t('attributes.createAttribute')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProductAttributesPage;